import React, { useState, useEffect } from "react";
import Button from "./Button";
import DropdownMenu from "./DropdownMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faHandshake,
  faChartPie,
  faTools,
  faMoneyBillWave,
  faRocket,
  faBriefcase,
  faGraduationCap,
  faHandshakeAngle,
  faBookOpen,
  faFlag,
  faBars,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faUserCowboy,
  faSackDollar,
  faUmbrellaBeach,
} from "@fortawesome/pro-regular-svg-icons";

interface MenuItem {
  icon: IconProp;
  title: string;
  subtext: string;
  url: string;
}

const Nav: React.FC = () => {
  const [toggledItem, setToggledItem] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(
    null,
  );

  const menuItems: { [key: string]: MenuItem[] } = {
    "For Operators": [
      {
        icon: faHandshake,
        title: "Join the Operator Network",
        subtext:
          "Learn, connect, and join a community of future owner-operators",
        url: "/join",
      },
      {
        icon: faMoneyBillWave,
        title: "Raise Capital",
        subtext: "From accredited & institutional investors",
        url: "/raise",
      },
      {
        icon: faTools,
        title: "Manage your Business",
        subtext:
          "Dedicated services to help you manage your business and shareholders",
        url: "/manage",
      },
    ],
    "For Investors": [
      {
        icon: faChartPie,
        title: "Why Invest in SMBs",
        subtext:
          "Grow your portfolio, earn income and invest in the future of our country",
        url: "/invest/why-smbs",
      },
      {
        icon: faBriefcase,
        title: "How it Works",
        subtext: "Learn how investing in SMBs through Mainshares works",
        url: "/invest/how-it-works",
      },
      {
        icon: faRocket,
        title: "Get Started",
        subtext: "Begin your journey on Mainshares today",
        url: "/invest",
      },
    ],
    "Get Involved": [
      {
        icon: faGraduationCap,
        title: "Become an Advisor",
        subtext: "Advise the next generation of owner-operators",
        url: "/advisors",
      },
      {
        icon: faHandshakeAngle,
        title: "Become a Partner",
        subtext: "Partner with us to back the next generation",
        url: "/partners",
      },
    ],
    "Resource Center": [
      {
        icon: faUserCowboy,
        title: "For Future Owners",
        subtext:
          "Step into business ownership with expert insights and actionable guidance",
        url: "/topic?series=forFutureOwners",
      },
      {
        icon: faSackDollar,
        title: "For Investors",
        subtext:
          "Smart strategies and market trends to make informed SMB investments",
        url: "/topic?series=forInvestors",
      },
      {
        icon: faUmbrellaBeach,
        title: "For Retiring Owners",
        subtext:
          "Seamlessly transition out of ownership with expert advice and planning",
        url: "/topic?series=forRetiringOwners",
      },
    ],
    "About Mainshares": [
      {
        icon: faBookOpen,
        title: "Our Story",
        subtext: "Learn about why we exist and who we are",
        url: "/about",
      },
      {
        icon: faFlag,
        title: "Manifesto",
        subtext: "Our manifesto outlines our mission",
        url: "/manifesto",
      },
    ],
  };

  const navigationItems = Object.keys(menuItems);

  const navClasses = `
    fixed top-0 left-0 w-full z-50
    h-16 px-4
    ${
      mobileMenuOpen
        ? "bg-[#fcfbf8] border-[#e3e3de]"
        : "bg-[#04262b]/50 border-[#fcfbf8]/10"
    }
    border-b
    backdrop-blur-md
    flex items-center
    transition duration-300 ease-in-out
  `;

  // Adjust text color based on mobile menu state
  const textColor = mobileMenuOpen ? "text-[#04262b]" : "text-[#fcfbf8]";
  const chevronColor = mobileMenuOpen ? "#04262b" : "#fcfbf8";

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      setMobileMenuOpen(false);
      setMobileSubmenuOpen(null);
      setToggledItem(null);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <nav className={navClasses}>
      <div className="max-w-[1344px] w-full m-auto">
        {/* Logo */}
        <div className="flex items-center w-full justify-between">
          <div
            className="h-8 flex items-center cursor-pointer"
            onClick={() => (window.location.href = "/")} // Redirect to "/"
          >
            <div className="Logo w-[88px] h-8 relative">
              <img
                src={mobileMenuOpen ? "/logo-dark.svg" : "/logo-light.svg"}
                alt="Logo"
                className="w-full h-full"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4">
            {navigationItems.map((item) => (
              <div key={item} className="relative">
                <Button
                  label={item}
                  style="Transparent"
                  size="Small"
                  onClick={() =>
                    setToggledItem(toggledItem === item ? null : item)
                  }
                  icon={
                    <FontAwesomeIcon
                      icon={toggledItem === item ? faChevronUp : faChevronDown}
                      size="xs"
                      color={chevronColor}
                    />
                  }
                  textColor={textColor}
                  className="whitespace-nowrap"
                />
                {/* Dropdown Menu */}
                {toggledItem === item && (
                  <div
                    id={`${item}-menu`}
                    className="absolute mt-2 left-1/2 transform -translate-x-1/2"
                  >
                    <DropdownMenu
                      menuItems={menuItems[item]}
                      onClose={() => setToggledItem(null)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu and Sign In */}
          <div className="flex items-center gap-6">
            {!mobileMenuOpen && (
              <Button
                label="Sign in"
                style="White"
                size="Small"
                className="whitespace-nowrap"
                onClick={() => (window.location.href = "/auth/sign-in")} // Redirect to "/auth/sign-in"
              />
            )}
            <div className="lg:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <FontAwesomeIcon
                  icon={mobileMenuOpen ? faXmark : faBars}
                  size="lg"
                  className={textColor}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div
          className="absolute top-16 left-0 w-full z-40 bg-[#fcfbf8]"
          onMouseLeave={() => setMobileMenuOpen(false)}
        >
          <div className="flex flex-col divide-y divide-[#ebebe5]">
            {navigationItems.map((item) => (
              <div key={item}>
                <div
                  className="p-4 flex justify-between items-center"
                  onClick={() =>
                    setMobileSubmenuOpen(
                      mobileSubmenuOpen === item ? null : item,
                    )
                  }
                >
                  <span className="text-base font-semibold text-[#04262b]">
                    {item}
                  </span>
                  <FontAwesomeIcon
                    icon={
                      mobileSubmenuOpen === item ? faChevronUp : faChevronDown
                    }
                    size="sm"
                    className="text-[#04262b]"
                  />
                </div>
                {/* Submenu Items */}
                {mobileSubmenuOpen === item && (
                  <div className="flex flex-col gap-1 px-4 pb-4">
                    {menuItems[item].map((menuItem) => (
                      <a
                        key={menuItem.title}
                        href={menuItem.url}
                        className="flex items-center gap-4 p-2.5 rounded"
                      >
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#ebebe5] rounded">
                          <FontAwesomeIcon
                            icon={menuItem.icon}
                            className="text-lg text-[#04262b]"
                            fixedWidth
                          />
                        </div>
                        <div className="flex flex-col">
                          <div className="text-[#04262b] text-sm font-semibold">
                            {menuItem.title}
                          </div>
                          <div className="text-[#717c79] text-sm font-medium">
                            {menuItem.subtext}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {/* Sign In Button */}
            <div className="p-4 border-t border-[#e3e3de]">
              <Button
                label="Sign in"
                style="Primary"
                size="Small"
                className="w-full whitespace-nowrap"
                onClick={() => (window.location.href = "/auth/sign-in")} // Redirect to "/auth/sign-in"
              />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Nav;
